import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Thermometer } from './Thermometer'
import styles from './FormStats.module.css'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'

export interface FormStat {
  option: string
  count: number
  percentage: number
}

interface AlarmStatus {
  medication: boolean
  food: boolean
  sleep: boolean
}

interface FormStatsProps {
  medicationStats: FormStat[]
  foodStats: FormStat[]
  sleepStats: FormStat[]
  alarmStatus: AlarmStatus
  userId: string
  isLoading?: boolean
}

const UNANSWERED_OPTION = 'No contestado'

const useFormStatistics = (stats: FormStat[]) => {
  return useMemo(() => {
    const answeredStats = stats.filter((s) => s.option !== UNANSWERED_OPTION)
    const totalAnswered = answeredStats.reduce((sum, s) => sum + s.count, 0)

    const getStatValue = (optionType: string): number => {
      if (totalAnswered === 0) return 0

      const stat = answeredStats.find((s) =>
        s.option.toLowerCase().includes(optionType.toLowerCase())
      )

      return stat ? (stat.count / totalAnswered) * 100 : 0
    }

    const unansweredCount = stats.find((s) => s.option === UNANSWERED_OPTION)?.count ?? 0

    return { getStatValue, unansweredCount }
  }, [stats])
}

const useAlarmSystem = (alarmStatus: AlarmStatus) => {
  const { t } = useTranslation()

  return useMemo(() => {
    const alarms: string[] = []

    if (alarmStatus.medication) {
      alarms.push(t('medication'))
    }

    if (alarmStatus.food) {
      alarms.push(t('food'))
    }

    if (alarmStatus.sleep) {
      alarms.push(t('sleep'))
    }

    const getMessage = () => {
      if (alarms.length === 0) return t('noActiveAlarms')

      const prefix = t('activeAlarm')

      if (alarms.length === 1) return `${prefix}: ${alarms[0]}`
      if (alarms.length === 2) return `${prefix}: ${alarms[0]} y ${alarms[1]}`

      return `${prefix}: ${alarms[0]}, ${alarms[1]} y ${alarms[2]}`
    }

    return {
      hasAlarms: alarms.length > 0,
      message: getMessage(),
    }
  }, [alarmStatus, t])
}

export const FormStats = ({
  medicationStats,
  foodStats,
  sleepStats,
  alarmStatus,
  userId,
  isLoading = false,
}: FormStatsProps) => {
  const { t } = useTranslation()
  const { openMenuLeft } = useIsOpenMenuLeftContext()

  const medication = useFormStatistics(medicationStats)
  const food = useFormStatistics(foodStats)
  const sleep = useFormStatistics(sleepStats)
  const alarm = useAlarmSystem(alarmStatus)

  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <Typography>{t('loading')}</Typography>
      </Box>
    )
  }

  return (
    <Box
      className={styles.container}
      style={{
        gap: openMenuLeft ? '20px' : '50px',
        padding: openMenuLeft ? '12px' : '16px',
      }}
    >
      <Box
        className={styles.titleBoxPatientStatistics}
        style={{
          flex: openMenuLeft ? '0 0 150px' : '0 0 auto',
          minWidth: openMenuLeft ? '180px' : 'auto',
        }}
      >
        <Box className={styles.titleContent}>
          <p className={styles.titleText}>{t('dailyResults')}</p>
          <p
            className={`${styles.titleAlarms} ${alarm.hasAlarms ? styles.activeAlarm : ''}`}
            style={{
              maxWidth: openMenuLeft ? '120px' : '200px',
            }}
          >
            {alarm.message}
          </p>
        </Box>
      </Box>

      <Box
        className={styles.thermometerContainer}
        style={{
          gap: openMenuLeft ? '2px' : '8px',
        }}
      >
        <Thermometer
          title={t('medication')}
          good={medication.getStatValue('yes')}
          regular={0}
          bad={medication.getStatValue('no')}
          notCompletedCount={medication.unansweredCount}
          showRegular={false}
          labels={{
            good: t('yes'),
            bad: t('no'),
          }}
        />

        <Thermometer
          title={t('food')}
          good={food.getStatValue('good')}
          regular={food.getStatValue('regular')}
          bad={food.getStatValue('bad')}
          notCompletedCount={food.unansweredCount}
        />

        <Thermometer
          title={t('sleep')}
          good={sleep.getStatValue('good')}
          regular={sleep.getStatValue('regular')}
          bad={sleep.getStatValue('bad')}
          notCompletedCount={sleep.unansweredCount}
        />
      </Box>
    </Box>
  )
}
